import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import Sidebar from "./components/layout/Sidebar";
import TopNavDateTime from "./components/layout/TopNavDateTime";
import CareDashboardPage from "./components/careHub/CareDashboardPage";
import CareTriagePage from "./components/careHub/CareTriagePage";
import Dashboard from "./components/Dashboard";
import ClientManagement from "./components/ClientManagement";
import LocationsManagement from "./components/LocationsManagement";
import UsersManagement from "./components/UsersManagement";
import CaregiversNursesManagement from "./components/CaregiversNursesManagement";
import TermsOfUse from "./components/TermsOfUse";
import PrivacyPolicy from "./components/PrivacyPolicy"; // Import PrivacyPolicy
import ClientView from "./components/ClientView"; // Import ClientView
import FolderView from "./components/FolderView"; // Import FolderView
import ScreenLockModal from "./components/ScreenLockModal"; // Import ScreenLockModal
import AipAnalysis from "./components/AipAnalysis"; // Import AipAnalysis
import Appointments from "./components/Appointments";
import Login from "./components/Login";
import SetupAccount from "./components/SetupAccount"; // Import SetupAccount
import Analytics from "./components/Analytics"; // Import Analytics
import AskAlbert from "./components/AskAlbert"; // Import AskAlbert
import ComplianceManagement from "./components/ComplianceManagement"; // ✅ Compliance Management
import ComplianceFolderView from "./components/ComplianceFolderView"; // ✅ Compliance Folder View
import PrivateRoute from "./components/PrivateRoute";
import { resolvePostLoginPath } from "./utils/postLoginRedirect";
import { resolveOrganizationDisplayName } from "./utils/organizationDisplayName";
import { syncStoredUserFeatureClaims } from "./utils/syncUserFeatureClaims";
import ErrorBoundary from "./components/ErrorBoundary"; // ✅ Import ErrorBoundary
import AiHiringOverview from "./components/aiHiring/AiHiringOverview";
import AiHiringCandidates from "./components/aiHiring/AiHiringCandidates";
import AiHiringSettings from "./components/aiHiring/AiHiringSettings";
import AiHiringDisabled from "./components/aiHiring/AiHiringDisabled";
import HiringReviewQueue from "./components/aiHiring/HiringReviewQueue";
import AiHiringDripCampaigns from "./components/aiHiring/AiHiringDripCampaigns";
import Agent247Settings from "./components/agent247/Agent247Settings";
import SupportTickets from "./components/SupportTickets";
import AlbertAiAlertsPage from "./components/agentAlbert/AlbertAiAlertsPage";
import AiKathyPage from "./components/aiKathy/AiKathyPage";
import AipChangeCenterPage from "./components/AipChangeCenterPage";
import WellskySyncReport from "./components/integrations/wellsky/WellskySyncReport";
import "./components/integrations/wellsky/WellskyIntegrations.css";
import "./components/aiHiring/AiHiring.css";

import {
  faTachometerAlt,
  faCalendarAlt,
  faUsers,
  faBriefcase,
  faChartBar,
  faHandsHelping,
  faQuestionCircle,
  faShieldAlt,
  faMapMarkerAlt,
  faUserFriends,
  faUserNurse,
  faFileContract,
  faChevronDown,
  faChevronUp,
  faChevronLeft,
  faChevronRight,
  faMapPin,
  faBuilding,
  faPlus,
  faLock,
  faSignOutAlt,
  faFileShield, // ✅ Compliance icon
  faRobot,
} from "@fortawesome/free-solid-svg-icons";
import AipSmartCare from "./components/AipSmartCare";
import PaymentBilling from "./components/PaymentBilling";
import QuestionSets from "./components/QuestionSets";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fetchLocations } from "./utils/api"; // Import API
import { API_URL } from "./apiConfig";
import { useAIHiringPlan } from "./hooks/useAIHiringPlan";

/** Client Snapshot (AipAnalysis) routes — sidebar auto-collapses on these only */
const isClientSnapshotRoute = (pathname = "") =>
  /^\/client-management\/client\/[^/]+\/aip-analysis(?:\/|$)/.test(pathname) ||
  /^\/aip-smart-care\/client\/[^/]+(?:\/|$)/.test(pathname);

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeMenuItem, setActiveMenuItem] = useState("Dashboard");
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
  const [isOtherDropdownOpen, setIsOtherDropdownOpen] = useState(false);
  const [isOtherAiHiringOpen, setIsOtherAiHiringOpen] = useState(false);

  // ✅ Global App Ready Gate
  const [appReady, setAppReady] = useState(false);
  const [authBootstrapped, setAuthBootstrapped] = useState(false);

  // Screen lock state with persistence
  const [isLocked, setIsLocked] = useState(() => {
    const stored = sessionStorage.getItem("screenLocked");
    console.log("Initializing screen lock from sessionStorage:", stored);
    return stored === "true";
  });

  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  const locationDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);
  const adminDropdownRef = useRef(null);
  const otherDropdownRef = useRef(null);
  const isAppointmentsPage = location.pathname.includes("/appointments");

  // Current logged-in user from localStorage
  const [currentUser, setCurrentUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });

  // ✅ Token state - synced with localStorage
  const [authToken, setAuthToken] = useState(() => {
    return localStorage.getItem("token");
  });

  // Fetch locations from API on mount
  const [allLocations, setAllLocations] = useState([]);
  const [currentActiveLocation, setCurrentActiveLocation] = useState(null);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(true);

  const [clientFolders, setClientFolders] = useState([]);
  const [reviewQueueCount, setReviewQueueCount] = useState(0);
  const companyDisplayName = resolveOrganizationDisplayName(
    currentActiveLocation,
    currentUser,
  );
  const aiHiringEnabled = Boolean(currentActiveLocation?.aiHiringEnabled);
  const wellSkyReportVisible = Boolean(
    currentActiveLocation?.wellSkyIntegrationEnabled,
  );
  const aiHiringOnly = Boolean(currentUser?.aiHiringOnly);
  const aiHiringAccessEnabled = aiHiringOnly || aiHiringEnabled;

  // Feature-scoped access (User.featureAccess — "Agent247 Only" / "AI Hiring
  // Only" restricted users). Agency-level aiHiringOnly folds into the same
  // "restrictedFeatures" concept as a legacy special case so the sidebar/route
  // guard below has one unified code path instead of two parallel ones.
  const userFeatureAccess = Array.isArray(currentUser?.featureAccess)
    ? currentUser.featureAccess.filter(Boolean)
    : [];
  const restrictedFeatures = aiHiringOnly
    ? Array.from(new Set([...userFeatureAccess, "ai_hiring"]))
    : userFeatureAccess;
  const isFeatureRestricted = restrictedFeatures.length > 0;
  const FEATURE_PATH_PREFIXES = {
    agent247: ["/agent247"],
    ai_hiring: ["/ai-hiring"],
  };
  const UNIVERSAL_CLIENT_PATHS = ["/support-tickets"];
  const FEATURE_HOME_PATH = {
    agent247: "/agent247",
    ai_hiring: "/ai-hiring",
  };
  const featureHomePath = FEATURE_HOME_PATH[restrictedFeatures[0]] || "/";
  const featureHomeMenuLabel = restrictedFeatures.includes("agent247")
    ? "AI Call Assist"
    : restrictedFeatures.includes("ai_hiring")
      ? "AI Hiring"
      : "Dashboard";
  const aiHiringLocationId = currentActiveLocation?.id || currentActiveLocation?._id;
  const { planActive: aiHiringPlanActive } = useAIHiringPlan(aiHiringLocationId);
  const AI_HIRING_PLAN_REDIRECT_PATH = "/features/ai-hiring/interview-settings";

  const isAiHiringPathActive = (path) => {
    if (path === "/ai-hiring/settings") {
      return (
        location.pathname.includes("/ai-hiring/settings") ||
        location.pathname.includes("/features/ai-hiring/interview-settings")
      );
    }
    return location.pathname.includes(path);
  };

  const isAiHiringSectionLocked = (requiresPlan = true) =>
    Boolean(requiresPlan) && !aiHiringPlanActive;

  const getAiHiringTargetPath = (path, requiresPlan = true) =>
    isAiHiringSectionLocked(requiresPlan) ? AI_HIRING_PLAN_REDIRECT_PATH : path;

  const getAiHiringItemClassName = (
    baseClassName,
    path,
    requiresPlan = true,
  ) =>
    [
      baseClassName,
      isAiHiringPathActive(path) ? "active" : "",
      isAiHiringSectionLocked(requiresPlan)
        ? "opacity-40 top-nav-dropdown-item-locked"
        : "",
    ]
      .filter(Boolean)
      .join(" ");

  const isCareTeamAccount = (user) => {
    const accountType = String(user?.accountType || "").toLowerCase();
    return accountType === "caregiver" || accountType === "nurse";
  };

  const handleLogout = () => {
    console.log("🚪 [App] User logged out");
    // Clear auth + app state
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("refreshTokenExpiresAt");
      localStorage.removeItem("user");
      localStorage.removeItem("activeLocation");
      sessionStorage.clear();
    } catch (err) {
      console.warn("Failed to clear storage on logout", err);
    }

    // Reset in-memory state
    setAuthToken(null);
    setCurrentUser(null);
    setCurrentActiveLocation(null);
    setAllLocations([]);
    setClientFolders([]);

    // Force hard navigation to login to drop any lingering component state
    window.location.href = "/login";
  };

  useEffect(() => {
    if (!currentUser || !isCareTeamAccount(currentUser)) return;

    console.warn(
      "🚫 [App] Caregiver/Nurse blocked from web dashboard:",
      currentUser?.email,
    );
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("refreshTokenExpiresAt");
      localStorage.removeItem("user");
      localStorage.removeItem("activeLocation");
      sessionStorage.clear();
    } catch (err) {
      console.warn("Failed to clear storage on caregiver/nurse block", err);
    }
    setAuthToken(null);
    setCurrentUser(null);
    setCurrentActiveLocation(null);
    setAllLocations([]);
    setClientFolders([]);
    if (location.pathname !== "/login") {
      navigate("/login?mobileOnly=1", { replace: true });
    }
  }, [currentUser, location.pathname, navigate]);

  // Frontend route guard for feature-restricted users: blocks direct-URL
  // navigation to any page outside the user's permitted feature area(s), not
  // just hiding the nav link (the failure pattern from the video-interview
  // flag bug this design is deliberately avoiding). This is UX/defense-in-
  // depth only — the real security boundary is the backend's
  // middleware/featureAccessGate.js, which 403s the underlying API calls
  // regardless of what the browser URL bar shows.
  useEffect(() => {
    if (!isFeatureRestricted) return;
    const isAuthPage =
      location.pathname === "/login" || location.pathname === "/setup-account";
    if (isAuthPage) return;
    const isAllowed =
      UNIVERSAL_CLIENT_PATHS.some((prefix) => location.pathname.startsWith(prefix)) ||
      restrictedFeatures.some((feature) =>
        (FEATURE_PATH_PREFIXES[feature] || []).some((prefix) =>
          location.pathname.startsWith(prefix),
        ),
      );
    if (!isAllowed) {
      navigate(featureHomePath, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFeatureRestricted, restrictedFeatures.join(","), location.pathname, navigate]);

  // Keep cached user.featureAccess aligned with the JWT (admin may change access
  // while the user still has an old localStorage copy).
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    const merged = syncStoredUserFeatureClaims(token);
    if (merged) setCurrentUser(merged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Persistent auth bootstrap (web): if access token is missing but refresh token exists,
  // silently refresh and avoid flashing the login screen.
  useEffect(() => {
    const tryAutoLogin = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          setAuthToken(token);
          setAuthBootstrapped(true);
          return;
        }

        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) {
          setAuthToken(null);
          setAuthBootstrapped(true);
          return;
        }

        const expAtRaw = localStorage.getItem("refreshTokenExpiresAt");
        const expAt = expAtRaw ? Number(expAtRaw) : null;
        if (expAt && Number.isFinite(expAt) && Date.now() > expAt) {
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("refreshTokenExpiresAt");
          localStorage.removeItem("user");
          localStorage.removeItem("activeLocation");
          setAuthToken(null);
          setCurrentUser(null);
          setAuthBootstrapped(true);
          return;
        }

        const resp = await fetch(`${API_URL}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data?.token) {
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("refreshTokenExpiresAt");
          localStorage.removeItem("user");
          localStorage.removeItem("activeLocation");
          sessionStorage.clear();
          setAuthToken(null);
          setCurrentUser(null);
          setAuthBootstrapped(true);
          return;
        }

        localStorage.setItem("token", data.token);
        setAuthToken(data.token);
        const merged = syncStoredUserFeatureClaims(data.token, {
          featureAccess: Array.isArray(data.featureAccess)
            ? data.featureAccess.filter(Boolean)
            : undefined,
          aiHiringOnly: data.aiHiringOnly,
        });
        if (merged) setCurrentUser(merged);
        setAuthBootstrapped(true);
      } catch (e) {
        try {
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("refreshTokenExpiresAt");
          localStorage.removeItem("user");
          localStorage.removeItem("activeLocation");
          sessionStorage.clear();
        } catch {
          // ignore
        }
        setAuthToken(null);
        setCurrentUser(null);
        setAuthBootstrapped(true);
      }
    };
    tryAutoLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sidebar collapsed state (persisted)
  const STORAGE_KEY = "albert_sidebar_collapsed";
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
    } catch (e) {
      console.warn("Failed to persist sidebar state", e);
    }
  }, [collapsed]);

  // Auto-collapse sidebar on Client Snapshot; restore prior state when leaving
  const sidebarPreAutoCollapseRef = useRef(null);

  useEffect(() => {
    const onClientSnapshot = isClientSnapshotRoute(location.pathname);

    if (onClientSnapshot) {
      if (sidebarPreAutoCollapseRef.current === null) {
        setCollapsed((current) => {
          sidebarPreAutoCollapseRef.current = current;
          return current ? current : true;
        });
      }
      return;
    }

    if (sidebarPreAutoCollapseRef.current !== null) {
      const restoreCollapsed = sidebarPreAutoCollapseRef.current;
      sidebarPreAutoCollapseRef.current = null;
      setCollapsed(restoreCollapsed);
    }
  }, [location.pathname]);

  // Function to load locations - can be called after login too
  // Wrapped in useCallback to safely include in useEffect dependencies
  const loadLocations = React.useCallback(async (retryCount = 0) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLocationsLoading(false);
      setAppReady(true);
      return;
    }

    try {
      setLocationsLoading(true);
      const locations = await fetchLocations();
      setAllLocations(locations);

      // Set initial active location
      const storedLocation = JSON.parse(localStorage.getItem("activeLocation"));

      let activeLoc = null;
      if (storedLocation) {
        const found = locations.find(
          (l) => l.id === storedLocation.id || l._id === storedLocation._id,
        );
        if (found) activeLoc = found;
      }

      if (!activeLoc && locations.length > 0) {
        const isMainOffice = (loc) =>
          /main\s*office/i.test(String(loc?.name || ""));
        activeLoc =
          locations.find((l) => l.status === "Active" && isMainOffice(l)) ||
          locations.find(isMainOffice) ||
          locations.find((l) => l.status === "Active") ||
          locations[0];
      }

      if (activeLoc) {
        const activeId = activeLoc.id || activeLoc._id;
        const updatedLocations = locations.map((l) => {
          const lId = l.id || l._id;
          return lId === activeId
            ? { ...l, isCurrent: true }
            : { ...l, isCurrent: false };
        });
        setAllLocations(updatedLocations);
        setCurrentActiveLocation({ ...activeLoc, isCurrent: true });
        // Save to localStorage for persistence
        localStorage.setItem("activeLocation", JSON.stringify(activeLoc));
        window.dispatchEvent(
          new CustomEvent("activeLocationUpdated", { detail: activeLoc }),
        );
      } else {
        setAllLocations(locations);
      }
      setLocationsLoading(false);
      setAppReady(true);
    } catch (error) {
      console.error("Failed to load locations:", error);
      // Retry up to 3 times with exponential backoff
      if (retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 500; // 500ms, 1s, 2s
        setTimeout(() => loadLocations(retryCount + 1), delay);
      } else {
        setLocationsLoading(false);
        setAppReady(true); // Even on failure, we must let the app render (to show error or empty state)
      }
    }
  }, []);

  // ✅ Load locations after login and also after navigation off /login
  const hasLoadedLocationsRef = React.useRef(false);
  useEffect(() => {
    const isLoginPage = location.pathname === "/login";
    const isSetupPage = location.pathname === "/setup-account";

    if (!authBootstrapped) {
      return;
    }

    if (!authToken) {
      setLocationsLoading(false);
      setAppReady(true);
      return;
    }

    if (isLoginPage || isSetupPage) {
      // Wait until we navigate off auth pages
      return;
    }

    if (hasLoadedLocationsRef.current && allLocations.length > 0) {
      setAppReady(true);
      return;
    }

    console.log(
      "✅ [App] Authenticated and off login/setup - loading locations",
    );
    hasLoadedLocationsRef.current = true;
    loadLocations();
  }, [authBootstrapped, authToken, location.pathname, loadLocations, allLocations.length]);

  // If current location is missing from list, send user home
  useEffect(() => {
    if (allLocations.length === 0) return;
    if (!currentActiveLocation) {
      navigate(isFeatureRestricted ? featureHomePath : "/");
      setActiveMenuItem(isFeatureRestricted ? featureHomeMenuLabel : "Dashboard");
      return;
    }
    const exists = allLocations.some(
      (l) =>
        (l.id || l._id) ===
        (currentActiveLocation.id || currentActiveLocation._id),
    );
    if (!exists) {
      navigate(isFeatureRestricted ? featureHomePath : "/");
      setActiveMenuItem(isFeatureRestricted ? featureHomeMenuLabel : "Dashboard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFeatureRestricted, featureHomePath, allLocations, currentActiveLocation, navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(event.target)
      ) {
        setIsLocationDropdownOpen(false);
      }
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target)
      ) {
        setIsUserDropdownOpen(false);
      }
      if (
        adminDropdownRef.current &&
        !adminDropdownRef.current.contains(event.target)
      ) {
        setIsAdminDropdownOpen(false);
      }
      if (
        otherDropdownRef.current &&
        !otherDropdownRef.current.contains(event.target)
      ) {
        setIsOtherDropdownOpen(false);
        setIsOtherAiHiringOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCreateClient = (newClientData) => {
    // This is now handled by API in FolderView, but we keep state for optimistic updates or global context if needed
    // For now, FolderView manages its own state refetching
  };

  const handleCreateFolder = (newFolderData) => {
    // This is passed to ClientManagement but now handled internally there via API
  };

  // Persist lock state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("screenLocked", String(isLocked));
    console.log("Screen lock state saved:", isLocked); // Debug log
  }, [isLocked]);

  // Inactivity timer logic
  useEffect(() => {
    let inactivityTimeout;
    const resetTimeout = () => {
      clearTimeout(inactivityTimeout);
      inactivityTimeout = setTimeout(() => setIsLocked(true), 60 * 100000); // 1 minute (extended for dev)
    };

    const handleActivity = () => {
      if (!isLocked) {
        resetTimeout();
      }
    };

    // Add event listeners for user activity
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("click", handleActivity);

    resetTimeout(); // Start the initial timeout

    // Cleanup event listeners on component unmount
    return () => {
      clearTimeout(inactivityTimeout);
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("click", handleActivity);
    };
  }, [isLocked]); // Re-run effect if lock state changes

  useEffect(() => {
    if (!authToken || !aiHiringAccessEnabled) {
      setReviewQueueCount(0);
      return;
    }
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/admin/review-queue`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok) throw new Error("failed");
        const payload = await res.json();
        const count = Array.isArray(payload) ? payload.length : payload?.items?.length || 0;
        if (active) setReviewQueueCount(count);
      } catch {
        if (active) setReviewQueueCount(0);
      }
    })();
    return () => {
      active = false;
    };
  }, [authToken, aiHiringAccessEnabled, currentActiveLocation?.id, currentActiveLocation?._id]);

  useEffect(() => {
    // Determine active menu item based on current path
    const path = location.pathname;
    if (aiHiringOnly) {
      setActiveMenuItem("AI Hiring");
      setIsAdminDropdownOpen(false);
      setIsOtherDropdownOpen(false);
      return;
    }
    const isOtherPath =
      path.includes("/ai-hiring/") ||
      path.includes("/features/ai-hiring/") ||
      path.includes("/agent247") ||
      path.includes("/albertai-alerts") ||
      path.includes("/ai-kathy") ||
      path.includes("/question-sets") ||
      path.includes("/analytics") ||
      path.includes("/aip-smart-care") ||
      path.includes("/wellsky-sync");

    if (path.includes("/admin/locations")) {
      setActiveMenuItem("Locations");
      setIsAdminDropdownOpen(true);
      setIsOtherDropdownOpen(false);
    } else if (path.includes("/admin/users")) {
      setActiveMenuItem("Users");
      setIsAdminDropdownOpen(true);
      setIsOtherDropdownOpen(false);
    } else if (path.includes("/admin/caregivers")) {
      setActiveMenuItem("Caregivers");
      setIsAdminDropdownOpen(true);
      setIsOtherDropdownOpen(false);
    } else if (path.includes("/admin/nurses")) {
      setActiveMenuItem("Nurses");
      setIsAdminDropdownOpen(true);
      setIsOtherDropdownOpen(false);
    } else if (path.includes("/admin/terms-of-use")) {
      setActiveMenuItem("Terms-of-use");
      setIsAdminDropdownOpen(true);
      setIsOtherDropdownOpen(false);
    } else if (path.includes("/admin/privacy-policy")) {
      setActiveMenuItem("Privacy-policy");
      setIsAdminDropdownOpen(true);
      setIsOtherDropdownOpen(false);
    } else if (path.includes("/appointments")) {
      setActiveMenuItem("Appointments");
      setIsAdminDropdownOpen(false);
      setIsOtherDropdownOpen(false);
    } else if (path.includes("/client-management")) {
      setActiveMenuItem("Client Management");
      setIsAdminDropdownOpen(false);
      setIsOtherDropdownOpen(false);
    } else if (path.includes("/compliance-management")) {
      setActiveMenuItem("Compliance Management");
      setIsAdminDropdownOpen(false);
      setIsOtherDropdownOpen(false);
    } else if (path.includes("/ask-albert")) {
      setActiveMenuItem("Ask Albert");
      setIsAdminDropdownOpen(false);
      setIsOtherDropdownOpen(false);
    } else if (isOtherPath) {
      setActiveMenuItem("Other");
      setIsAdminDropdownOpen(false);
      setIsOtherDropdownOpen(false);
    } else {
      setActiveMenuItem("Dashboard");
      setIsAdminDropdownOpen(false);
      setIsOtherDropdownOpen(false);
    }
  }, [aiHiringOnly, location.pathname]);

  const handleLocationChange = (selectedLocation) => {
    // Ensure we have a valid ID to compare
    const selectedId = selectedLocation.id || selectedLocation._id;

    if (!selectedId) {
      console.error("Selected location has no ID:", selectedLocation);
      return;
    }

    const updatedLocations = allLocations.map((loc) => {
      const locId = loc.id || loc._id;
      return locId === selectedId
        ? { ...loc, isCurrent: true }
        : { ...loc, isCurrent: false };
    });

    setAllLocations(updatedLocations);
    // Ensure the stored object also has isCurrent: true
    const newActive = { ...selectedLocation, isCurrent: true };
    setCurrentActiveLocation(newActive);
    localStorage.setItem("activeLocation", JSON.stringify(newActive));
    window.dispatchEvent(
      new CustomEvent("activeLocationUpdated", { detail: newActive }),
    );
    setIsLocationDropdownOpen(false);
    // Route based on access mode when switching locations
    navigate(isFeatureRestricted ? featureHomePath : "/");
    setActiveMenuItem(isFeatureRestricted ? "AI Hiring" : "Dashboard");
  };

  const handleUserUpdated = (updatedUser) => {
    setCurrentUser(updatedUser);
    try {
      localStorage.setItem("user", JSON.stringify(updatedUser));
    } catch (e) {
      console.warn("Failed to persist user update", e);
    }
  };

  const bannerProps = {
    activeLocation: currentActiveLocation,
    locations: allLocations,
    onChangeLocation: handleLocationChange,
    companyDisplayName,
    currentUser,
    onLogout: handleLogout,
    onLock: () => setIsLocked(true),
    onUserUpdated: handleUserUpdated,
  };

  // If on login page, render only the Login component (no sidebar/header)
  if (location.pathname === "/login") {
    return (
      <Login
        onLogin={(user) => {
          console.log("✅ [App] User logged in:", user.name);

          // Update current user
          setCurrentUser(user);

          // ✅ Update token state - this triggers the useEffect to load locations
          const token = localStorage.getItem("token");
          setAuthToken(token);
          console.log(
            "✅ [App] Token state updated, useEffect will load locations",
          );

          // Clear lock state on login
          sessionStorage.removeItem("screenLocked");
          // Redirect by access mode after login (or deep link from email)
          const returnTo = new URLSearchParams(window.location.search).get(
            "returnTo",
          );
          navigate(resolvePostLoginPath(user, returnTo) || "/dashboard");
        }}
      />
    );
  }

  // If on setup account page, render only the Setup component (no sidebar/header)
  if (location.pathname === "/setup-account") {
    return (
      <PrivateRoute>
        <SetupAccount />
      </PrivateRoute>
    );
  }

  // Check authentication first
  const token = localStorage.getItem("token");
  if (
    !token &&
    authBootstrapped &&
    location.pathname !== "/login" &&
    location.pathname !== "/setup-account"
  ) {
    // Not authenticated and not on login page - redirect to login
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    navigate(`/login?returnTo=${returnTo}`);
    return null;
  }

  // ✅ Global App Ready Gate
  // Show loading until appReady is true (or while locations are loading)
  if (!authBootstrapped || !appReady || (locationsLoading && token)) {
    return (
      <div
        className="app-loading"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: "16px",
          background: "#0f172a",
        }}
      >
        <img src="/new-logo.png" alt="Albert AI" style={{ width: "150px" }} />
        <div style={{ color: "#94a3b8" }}>Loading your workspace...</div>
      </div>
    );
  }

  // If authenticated but no locations yet, allow app to load
  // The dashboard and other components will handle empty location state
  if (!currentActiveLocation && allLocations.length > 0) {
    setCurrentActiveLocation(allLocations[0]);
  }

  return (
    <div className="app-container">
      <div className="app-sidebars">
        <Sidebar
          wellSkyReportVisible={wellSkyReportVisible}
          aiHiringOnly={aiHiringOnly}
          restrictedFeatures={restrictedFeatures}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((c) => !c)}
          activeLocation={currentActiveLocation}
          locations={allLocations}
          currentUser={currentUser}
        />
      </div>
      <div className="main-area">
      <div className="top-nav">
        <div className="top-nav-row top-nav-row-primary">
          <div className="top-nav-left">
            {/* Logo lives in the Sidebar (dark background renders it correctly).
                The white header shows only the location name and user controls. */}
          </div>
          <div className="top-nav-right">
            <button
              type="button"
              className="top-nav-icon-btn"
              onClick={() => setIsLocked(true)}
              aria-label="Lock screen"
            >
              <FontAwesomeIcon icon={faLock} />
            </button>
            <div
              className="location-switcher top-nav-location"
              ref={locationDropdownRef}
              onClick={() =>
                setIsLocationDropdownOpen(!isLocationDropdownOpen)
              }
            >
              <FontAwesomeIcon icon={faMapPin} className="location-pin-icon" />
              <span className="current-location-name">
                {currentActiveLocation?.name || "Select Location"}
              </span>
              <FontAwesomeIcon
                icon={isLocationDropdownOpen ? faChevronUp : faChevronDown}
                className="location-dropdown-arrow-icon"
              />
              {isLocationDropdownOpen && (
                <div className="location-dropdown-menu">
                  {companyDisplayName ? (
                    <div className="location-dropdown-company-header">
                      {companyDisplayName}
                    </div>
                  ) : null}
                  {allLocations.length > 0 && (
                    <hr className="location-divider" />
                  )}
                  {allLocations.map((loc) => (
                    <div
                      key={loc.id}
                      className="location-dropdown-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLocationChange(loc);
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faBuilding}
                        className="location-item-icon"
                      />
                      <div className="location-item-details">
                        <span className="location-item-name">
                          {loc.name}{" "}
                          {loc.isCurrent && (
                            <span className="current-tag">Current</span>
                          )}
                        </span>
                        <span className="location-item-address">
                          {[loc.address, loc.city, loc.state, loc.zip]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      </div>
                    </div>
                  ))}
                  <hr className="location-divider" />
                  <div
                    className="location-dropdown-item manage-locations"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/admin/locations");
                      setIsLocationDropdownOpen(false);
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faPlus}
                      className="manage-locations-icon"
                    />
                    <span>Manage Locations</span>
                  </div>
                </div>
              )}
            </div>
            <div
              className="top-nav-user-block"
              ref={userDropdownRef}
              onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
            >
              <div className="user-profile-icon top-nav-user" aria-hidden="true">
                {currentUser?.name
                  ?.split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase() || "U"}
              </div>
              <div className="top-nav-user-meta">
                <div className="top-nav-user-name">
                  <span>{currentUser?.name || "User"}</span>
                  <FontAwesomeIcon
                    icon={isUserDropdownOpen ? faChevronUp : faChevronDown}
                    className="top-nav-user-caret"
                  />
                </div>
                <TopNavDateTime className="top-nav-user-datetime" />
              </div>
              {isUserDropdownOpen && (
                <div className="user-dropdown-menu">
                  <div
                    className="user-info-section"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="user-name">{currentUser?.name || "User"}</div>
                    <div className="user-email">{currentUser?.email || ""}</div>
                    <div className="user-role">
                      {currentUser?.role?.toUpperCase() || "USER"}
                    </div>
                  </div>
                  <div
                    className="logout-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log("🚪 [App] User logged out");

                      // Clear all auth data
                      localStorage.removeItem("token");
                      localStorage.removeItem("refreshToken");
                      localStorage.removeItem("user");
                      localStorage.removeItem("activeLocation");
                      sessionStorage.removeItem("screenLocked");

                      // Clear token state
                      setAuthToken(null);
                      setCurrentUser(null);

                      // Redirect to login
                      navigate("/login");
                      setIsUserDropdownOpen(false);
                    }}
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} className="logout-icon" />{" "}
                    Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Navigation moved to Sidebar component */}
        {false && false && (
              <li
                className={`top-nav-item top-nav-dropdown ${
                  isOtherAiHiringOpen ? "open" : ""
                } ${
                  location.pathname.includes("/ai-hiring/") ||
                  location.pathname.includes("/features/ai-hiring/")
                    ? "active"
                    : ""
                }`}
              >
                <button
                  type="button"
                  className="top-nav-link"
                  onClick={() => setIsOtherAiHiringOpen((prev) => !prev)}
                >
                  AI Hiring
                  <FontAwesomeIcon
                    icon={isOtherAiHiringOpen ? faChevronUp : faChevronDown}
                    className="top-nav-caret"
                  />
                </button>
                {isOtherAiHiringOpen && (
                  <div className="top-nav-dropdown-menu">
                    <Link
                      to={getAiHiringTargetPath("/ai-hiring/overview", true)}
                      className={getAiHiringItemClassName(
                        "top-nav-dropdown-item",
                        "/ai-hiring/overview",
                        true,
                      )}
                      onClick={() => setIsOtherAiHiringOpen(false)}
                    >
                      Overview
                      {isAiHiringSectionLocked(true) ? (
                        <span aria-hidden="true">🔒</span>
                      ) : null}
                    </Link>
                    <Link
                      to={getAiHiringTargetPath("/ai-hiring/candidates", true)}
                      className={getAiHiringItemClassName(
                        "top-nav-dropdown-item",
                        "/ai-hiring/candidates",
                        true,
                      )}
                      onClick={() => setIsOtherAiHiringOpen(false)}
                    >
                      Candidates
                      {isAiHiringSectionLocked(true) ? (
                        <span aria-hidden="true">🔒</span>
                      ) : null}
                    </Link>
                    <Link
                      to={getAiHiringTargetPath("/ai-hiring/drip-campaigns", true)}
                      className={getAiHiringItemClassName(
                        "top-nav-dropdown-item",
                        "/ai-hiring/drip-campaigns",
                        true,
                      )}
                      onClick={() => setIsOtherAiHiringOpen(false)}
                    >
                      Drip Campaigns
                      {isAiHiringSectionLocked(true) ? (
                        <span aria-hidden="true">🔒</span>
                      ) : null}
                    </Link>
                    <Link
                      to={getAiHiringTargetPath("/ai-hiring/review-queue", true)}
                      className={getAiHiringItemClassName(
                        "top-nav-dropdown-item",
                        "/ai-hiring/review-queue",
                        true,
                      )}
                      onClick={() => setIsOtherAiHiringOpen(false)}
                    >
                      Review Queue
                      {isAiHiringSectionLocked(true) ? (
                        <span aria-hidden="true">🔒</span>
                      ) : null}
                      {reviewQueueCount > 0 ? (
                        <span className="top-nav-count-badge">{reviewQueueCount}</span>
                      ) : null}
                    </Link>
                    <Link
                      to={getAiHiringTargetPath("/ai-hiring/settings", false)}
                      className={getAiHiringItemClassName(
                        "top-nav-dropdown-item",
                        "/ai-hiring/settings",
                        false,
                      )}
                      onClick={() => setIsOtherAiHiringOpen(false)}
                    >
                      Interview Settings
                    </Link>
                  </div>
                )}
              </li>
            )}
            {false && !aiHiringOnly && (
              <>
                <li
                  className={`top-nav-item ${
                    activeMenuItem === "Dashboard" ? "active" : ""
                  }`}
                >
                  <Link to="/" className="top-nav-link">
                    Dashboard
                  </Link>
                </li>
                <li
                  className={`top-nav-item ${
                    activeMenuItem === "Appointments" ? "active" : ""
                  }`}
                >
                  <Link to="/appointments" className="top-nav-link">
                    Client Scheduling
                  </Link>
                </li>
                <li
                  className={`top-nav-item ${
                    activeMenuItem === "Client Management" ? "active" : ""
                  }`}
                >
                  <Link to="/client-management" className="top-nav-link">
                    Client Mgmt
                  </Link>
                </li>
                <li
                  className={`top-nav-item ${
                    activeMenuItem === "Compliance Management" ? "active" : ""
                  }`}
                >
                  <Link to="/compliance-management" className="top-nav-link">
                    Compliance Mgmt
                  </Link>
                </li>
                <li
                  className={`top-nav-item ${
                    activeMenuItem === "Ask Albert" ? "active" : ""
                  }`}
                >
                  <Link to="/ask-albert" className="top-nav-link">
                    Ask Albert
                  </Link>
                </li>
                <li
                  className={`top-nav-item top-nav-dropdown ${
                    isAdminDropdownOpen ? "open" : ""
                  }`}
                  ref={adminDropdownRef}
                >
                  <button
                    type="button"
                    className="top-nav-link"
                    onClick={() => setIsAdminDropdownOpen((prev) => !prev)}
                  >
                    Admin
                    <FontAwesomeIcon
                      icon={isAdminDropdownOpen ? faChevronUp : faChevronDown}
                      className="top-nav-caret"
                    />
                  </button>
                  {isAdminDropdownOpen && (
                    <div className="top-nav-dropdown-menu">
                      <Link
                        to="/admin/locations"
                        className={`top-nav-dropdown-item ${
                          activeMenuItem === "Locations" ? "active" : ""
                        }`}
                        onClick={() => setIsAdminDropdownOpen(false)}
                      >
                        Locations
                      </Link>
                      <Link
                        to="/admin/users"
                        className={`top-nav-dropdown-item ${
                          activeMenuItem === "Users" ? "active" : ""
                        }`}
                        onClick={() => setIsAdminDropdownOpen(false)}
                      >
                        Users
                      </Link>
                      <Link
                        to="/admin/caregivers"
                        className={`top-nav-dropdown-item ${
                          activeMenuItem === "Caregivers" ? "active" : ""
                        }`}
                        onClick={() => setIsAdminDropdownOpen(false)}
                      >
                        Caregivers
                      </Link>
                      <Link
                        to="/admin/nurses"
                        className={`top-nav-dropdown-item ${
                          activeMenuItem === "Nurses" ? "active" : ""
                        }`}
                        onClick={() => setIsAdminDropdownOpen(false)}
                      >
                        Nurses
                      </Link>
                      <Link
                        to="/admin/terms-of-use"
                        className={`top-nav-dropdown-item ${
                          activeMenuItem === "Terms-of-use" ? "active" : ""
                        }`}
                        onClick={() => setIsAdminDropdownOpen(false)}
                      >
                        Terms of Use
                      </Link>
                      <Link
                        to="/admin/privacy-policy"
                        className={`top-nav-dropdown-item ${
                          activeMenuItem === "Privacy-policy" ? "active" : ""
                        }`}
                        onClick={() => setIsAdminDropdownOpen(false)}
                      >
                        Privacy Policy
                      </Link>
                      <button
                        type="button"
                        className="top-nav-dropdown-item logout"
                        onClick={() => {
                          setIsAdminDropdownOpen(false);
                          handleLogout();
                        }}
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </li>

                <li
                  className={`top-nav-item top-nav-dropdown ${
                    isOtherDropdownOpen ? "open" : ""
                  } ${activeMenuItem === "Other" ? "active" : ""}`}
                  ref={otherDropdownRef}
                >
              <button
                type="button"
                className="top-nav-link"
                onClick={() => {
                  setIsOtherDropdownOpen((prev) => !prev);
                  setIsAdminDropdownOpen(false);
                  setIsOtherAiHiringOpen(false);
                }}
              >
                Features
                <FontAwesomeIcon
                  icon={isOtherDropdownOpen ? faChevronUp : faChevronDown}
                  className="top-nav-caret"
                />
              </button>
              {isOtherDropdownOpen && (
                <div className="top-nav-dropdown-menu">
                  {aiHiringAccessEnabled && (
                    <>
                      <button
                        type="button"
                        className={`top-nav-dropdown-item top-nav-dropdown-parent ${
                          isOtherAiHiringOpen ? "open" : ""
                        } ${
                          location.pathname.includes("/ai-hiring/") ||
                          location.pathname.includes("/features/ai-hiring/")
                            ? "active"
                            : ""
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsOtherAiHiringOpen((prev) => !prev);
                        }}
                      >
                        AI Hiring
                        <FontAwesomeIcon
                          icon={isOtherAiHiringOpen ? faChevronUp : faChevronDown}
                          className="top-nav-caret"
                        />
                      </button>
                      {isOtherAiHiringOpen && (
                        <div className="top-nav-dropdown-submenu">
                          <Link
                            to={getAiHiringTargetPath("/ai-hiring/overview", true)}
                            className={getAiHiringItemClassName(
                              "top-nav-dropdown-item top-nav-dropdown-subitem",
                              "/ai-hiring/overview",
                              true,
                            )}
                            onClick={() => setIsOtherDropdownOpen(false)}
                          >
                            Overview
                            {isAiHiringSectionLocked(true) ? (
                              <span aria-hidden="true">🔒</span>
                            ) : null}
                          </Link>
                          <Link
                            to={getAiHiringTargetPath("/ai-hiring/candidates", true)}
                            className={getAiHiringItemClassName(
                              "top-nav-dropdown-item top-nav-dropdown-subitem",
                              "/ai-hiring/candidates",
                              true,
                            )}
                            onClick={() => setIsOtherDropdownOpen(false)}
                          >
                            Candidates
                            {isAiHiringSectionLocked(true) ? (
                              <span aria-hidden="true">🔒</span>
                            ) : null}
                          </Link>
                          <Link
                            to={getAiHiringTargetPath("/ai-hiring/drip-campaigns", true)}
                            className={getAiHiringItemClassName(
                              "top-nav-dropdown-item top-nav-dropdown-subitem",
                              "/ai-hiring/drip-campaigns",
                              true,
                            )}
                            onClick={() => setIsOtherDropdownOpen(false)}
                          >
                            Drip Campaigns
                            {isAiHiringSectionLocked(true) ? (
                              <span aria-hidden="true">🔒</span>
                            ) : null}
                          </Link>
                          <Link
                            to={getAiHiringTargetPath("/ai-hiring/review-queue", true)}
                            className={getAiHiringItemClassName(
                              "top-nav-dropdown-item top-nav-dropdown-subitem",
                              "/ai-hiring/review-queue",
                              true,
                            )}
                            onClick={() => setIsOtherDropdownOpen(false)}
                          >
                            Review Queue
                            {isAiHiringSectionLocked(true) ? (
                              <span aria-hidden="true">🔒</span>
                            ) : null}
                            {reviewQueueCount > 0 ? (
                              <span className="top-nav-count-badge">{reviewQueueCount}</span>
                            ) : null}
                          </Link>
                          <Link
                            to={getAiHiringTargetPath("/ai-hiring/settings", false)}
                            className={getAiHiringItemClassName(
                              "top-nav-dropdown-item top-nav-dropdown-subitem",
                              "/ai-hiring/settings",
                              false,
                            )}
                            onClick={() => setIsOtherDropdownOpen(false)}
                          >
                            Interview Settings
                          </Link>
                        </div>
                      )}
                      <div className="top-nav-dropdown-divider" />
                    </>
                  )}

                  <Link
                    to="/agent247"
                    className={`top-nav-dropdown-item ${
                      location.pathname.includes("/agent247") ? "active" : ""
                    }`}
                    onClick={() => setIsOtherDropdownOpen(false)}
                  >
                    24/7 Inquiry Call Assist
                  </Link>
                  <Link
                    to="/albertai-alerts"
                    className={`top-nav-dropdown-item ${
                      location.pathname.includes("/albertai-alerts") ? "active" : ""
                    }`}
                    onClick={() => setIsOtherDropdownOpen(false)}
                  >
                    Shift Report
                  </Link>
                  <Link
                    to="/ai-kathy"
                    className={`top-nav-dropdown-item ${
                      location.pathname.includes("/ai-kathy") ? "active" : ""
                    }`}
                    onClick={() => setIsOtherDropdownOpen(false)}
                  >
                    AI Kathy
                  </Link>
                  <Link
                    to="/question-sets"
                    className={`top-nav-dropdown-item ${
                      location.pathname.includes("/question-sets") ? "active" : ""
                    }`}
                    onClick={() => setIsOtherDropdownOpen(false)}
                  >
                    Question Sets
                  </Link>
                  <Link
                    to="/analytics"
                    className={`top-nav-dropdown-item ${
                      location.pathname.includes("/analytics") ? "active" : ""
                    }`}
                    onClick={() => setIsOtherDropdownOpen(false)}
                  >
                    Analytics & Reports
                  </Link>
                  <Link
                    to="/aip-smart-care"
                    className={`top-nav-dropdown-item ${
                      location.pathname.includes("/aip-smart-care") ? "active" : ""
                    }`}
                    onClick={() => setIsOtherDropdownOpen(false)}
                  >
                    Simulation
                  </Link>
                  {wellSkyReportVisible ? (
                    <Link
                      to="/wellsky-sync"
                      className={`top-nav-dropdown-item ${
                        location.pathname.includes("/wellsky-sync") ? "active" : ""
                      }`}
                      onClick={() => setIsOtherDropdownOpen(false)}
                    >
                      WellSky Sync & Reports
                    </Link>
                  ) : null}
                </div>
              )}
                </li>
              </>
            )}
      </div>
      <div className="main-content">
        <div className="page-content">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/setup-account" element={<SetupAccount />} />

            <Route
              path="/"
              element={
                <PrivateRoute>
                  {aiHiringOnly ? (
                    <Navigate to="/ai-hiring" replace />
                  ) : (
                    <Dashboard {...bannerProps} />
                  )}
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  {aiHiringOnly ? (
                    <Navigate to="/ai-hiring" replace />
                  ) : (
                    <Dashboard {...bannerProps} />
                  )}
                </PrivateRoute>
              }
            />

            <Route
              path="/ai-hiring"
              element={<Navigate to="/ai-hiring/overview" replace />}
            />
            <Route
              path="/ai-hiring/overview"
              element={
                <PrivateRoute>
                  {aiHiringAccessEnabled ? (
                    <AiHiringOverview {...bannerProps} />
                  ) : (
                    <AiHiringDisabled {...bannerProps} />
                  )}
                </PrivateRoute>
              }
            />
            <Route
              path="/ai-hiring/candidates"
              element={
                <PrivateRoute>
                  {aiHiringAccessEnabled ? (
                    <AiHiringCandidates {...bannerProps} />
                  ) : (
                    <AiHiringDisabled {...bannerProps} />
                  )}
                </PrivateRoute>
              }
            />
            <Route
              path="/ai-hiring/review-queue"
              element={
                <PrivateRoute>
                  {aiHiringAccessEnabled ? (
                    <HiringReviewQueue {...bannerProps} />
                  ) : (
                    <AiHiringDisabled {...bannerProps} />
                  )}
                </PrivateRoute>
              }
            />
            <Route
              path="/ai-hiring/drip-campaigns"
              element={
                <PrivateRoute>
                  {aiHiringAccessEnabled ? (
                    <AiHiringDripCampaigns {...bannerProps} />
                  ) : (
                    <AiHiringDisabled {...bannerProps} />
                  )}
                </PrivateRoute>
              }
            />
            <Route
              path="/ai-hiring/settings"
              element={
                <PrivateRoute>
                  {aiHiringAccessEnabled ? (
                    <AiHiringSettings {...bannerProps} />
                  ) : (
                    <AiHiringDisabled {...bannerProps} />
                  )}
                </PrivateRoute>
              }
            />
            <Route
              path="/features/ai-hiring/interview-settings"
              element={
                <PrivateRoute>
                  {aiHiringAccessEnabled ? (
                    <AiHiringSettings {...bannerProps} />
                  ) : (
                    <AiHiringDisabled {...bannerProps} />
                  )}
                </PrivateRoute>
              }
            />
            <Route
              path="/ai-hiring/questions"
              element={
                <PrivateRoute>
                  {aiHiringAccessEnabled ? (
                    <AiHiringSettings {...bannerProps} />
                  ) : (
                    <AiHiringDisabled {...bannerProps} />
                  )}
                </PrivateRoute>
              }
            />
            <Route
              path="/ai-hiring/reports"
              element={<Navigate to="/ai-hiring/overview" replace />}
            />
            <Route
              path="/support-tickets"
              element={
                <PrivateRoute>
                  <SupportTickets {...bannerProps} />
                </PrivateRoute>
              }
            />
            <Route
              path="/agent247"
              element={
                <PrivateRoute>
                  <Agent247Settings {...bannerProps} />
                </PrivateRoute>
              }
            />
            <Route
              path="/albertai-alerts"
              element={
                <PrivateRoute>
                  <AlbertAiAlertsPage {...bannerProps} />
                </PrivateRoute>
              }
            />
            <Route
              path="/ai-kathy"
              element={
                <PrivateRoute>
                  <AiKathyPage {...bannerProps} />
                </PrivateRoute>
              }
            />
            <Route
              path="/aip-change-center"
              element={
                <PrivateRoute>
                  <AipChangeCenterPage {...bannerProps} />
                </PrivateRoute>
              }
            />

            <Route
              path="/care-dashboard"
              element={
                <PrivateRoute>
                  <CareDashboardPage {...bannerProps} />
                </PrivateRoute>
              }
            />
            <Route
              path="/care/triage"
              element={
                <PrivateRoute>
                  <CareTriagePage {...bannerProps} />
                </PrivateRoute>
              }
            />
            <Route
              path="/care/assigned-cases"
              element={
                <PrivateRoute>
                  <CareTriagePage {...bannerProps} careHubView="assigned" />
                </PrivateRoute>
              }
            />

            <Route
              path="/appointments"
              element={
                <PrivateRoute>
                  <Appointments {...bannerProps} />
                </PrivateRoute>
              }
            />

            {/* ... other protected routes ... */}
            <Route
              path="/client-management"
              element={
                <PrivateRoute>
                  <ClientManagement
                    {...bannerProps}
                    clientFolders={clientFolders}
                    setClientFolders={setClientFolders}
                    onCreateFolder={handleCreateFolder}
                  />
                </PrivateRoute>
              }
            />

            {/* Compliance Management Routes */}
            <Route
              path="/compliance-management"
              element={
                <PrivateRoute>
                  <ComplianceManagement {...bannerProps} />
                </PrivateRoute>
              }
            />

            <Route
              path="/compliance-management/:folderId"
              element={
                <PrivateRoute>
                  <ComplianceFolderView {...bannerProps} />
                </PrivateRoute>
              }
            />

            {/* Analytics Route */}
            <Route
              path="/analytics"
              element={
                <PrivateRoute>
                  <Analytics {...bannerProps} />
                </PrivateRoute>
              }
            />

            {/* Ask Albert Route */}
            <Route
              path="/ask-albert"
              element={
                <PrivateRoute>
                  <AskAlbert {...bannerProps} />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/locations"
              element={
                <PrivateRoute>
                  <LocationsManagement
                    locations={allLocations}
                    setLocations={setAllLocations}
                    currentActiveLocation={currentActiveLocation}
                    setCurrentActiveLocation={setCurrentActiveLocation}
                    {...bannerProps}
                  />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <PrivateRoute>
                  <UsersManagement {...bannerProps} />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/caregivers"
              element={
                <PrivateRoute>
                  <CaregiversNursesManagement
                    roleFilter="Caregiver"
                    pageTitle="Caregivers"
                    {...bannerProps}
                  />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/nurses"
              element={
                <PrivateRoute>
                  <CaregiversNursesManagement
                    roleFilter="Nurse"
                    pageTitle="Nurses"
                    {...bannerProps}
                  />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/terms-of-use"
              element={
                <PrivateRoute>
                  <TermsOfUse {...bannerProps} />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/privacy-policy"
              element={
                <PrivateRoute>
                  <PrivacyPolicy {...bannerProps} />
                </PrivateRoute>
              }
            />

            <Route
              path="/wellsky-sync"
              element={
                <PrivateRoute>
                  {wellSkyReportVisible ? (
                    <div className="wellsky-integrations-root">
                      <WellskySyncReport {...bannerProps} />
                    </div>
                  ) : (
                    <Navigate to="/" replace />
                  )}
                </PrivateRoute>
              }
            />

            <Route
              path="/client-management/:folderId"
              element={
                <PrivateRoute>
                  <FolderView
                    onCreateFolder={handleCreateFolder}
                    clientFolders={clientFolders}
                    onCreateClient={handleCreateClient}
                    activeLocation={currentActiveLocation}
                    locations={allLocations}
                  />
                </PrivateRoute>
              }
            />
            <Route
              path="/client-management/location/:locationId"
              element={
                <PrivateRoute>
                  <FolderView
                    onCreateFolder={handleCreateFolder}
                    clientFolders={clientFolders}
                    onCreateClient={handleCreateClient}
                    activeLocation={currentActiveLocation}
                    locations={allLocations}
                    forceLocationView
                  />
                </PrivateRoute>
              }
            />

            <Route
              path="/client-management/client/:clientId"
              element={
                <PrivateRoute>
                  <ClientView />
                </PrivateRoute>
              }
            />

            <Route
              path="/client-management/client/:clientId/aip-analysis"
              element={
                <PrivateRoute>
                  <AipAnalysis />
                </PrivateRoute>
              }
            />
            <Route
              path="/aip-smart-care"
              element={
                <PrivateRoute>
                  <AipSmartCare {...bannerProps} />
                </PrivateRoute>
              }
            />
            <Route
              path="/aip-smart-care/client/:clientId"
              element={
                <PrivateRoute>
                  <AipAnalysis />
                </PrivateRoute>
              }
            />
            <Route
              path="/payment-billing"
              element={
                <PrivateRoute>
                  <PaymentBilling {...bannerProps} />
                </PrivateRoute>
              }
            />
            <Route
              path="/question-sets"
              element={
                <PrivateRoute>
                  <QuestionSets {...bannerProps} />
                </PrivateRoute>
              }
            />
          </Routes>
        </div>
      </div>
      </div> {/* /main-area */}

      {isLocked && (
        <ScreenLockModal
          onUnlock={() => {
            console.log("Unlocking screen");
            setIsLocked(false);
          }}
        />
      )}
    </div>
  );
};

const App = () => (
  <ErrorBoundary>
    <Router>
      <AppContent />
    </Router>
  </ErrorBoundary>
);

export default App;
// Fresh build
